import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    Box, 
    Container, 
    Paper, 
    Typography, 
    CircularProgress, 
    Grid,
    Button
} from '@mui/material';
import { useSelector } from 'react-redux';
import MainLayoutDemand from '../../MainLoyout/MainLayoutDemand';
import CampaignTrendsTable from './CampaignTrendsTable';
import CampaignTable from './CampaignTable';
import DashboardHeader from './DashboardHeader';
import MetricsCards from './MetricsCards';
import TableControls from './TableControls';
import { useMediaBuyingReports, useCampaignStatuses, useCampaignSelection } from './hooks';
import { hasLowPerformance, calculateTotals, calculateMetricTrends, calculateRoas } from './utils';


/**
 * Media Buying Report Panel Component
 */
const MediaBuyingReportPanel = () => {
    // Get admin status from Redux store
    const isAdmin = useSelector((state) => state.users.is_admin);
    
    // State for date range and filters
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
        preset: 'last7Days'
    });
    const [filters, setFilters] = useState({});
    const [sortOptions, setSortOptions] = useState({
        field: 'revenue',
        direction: 'desc'
    });
    // Add state for low performance filter
    const [showLowPerformance, setShowLowPerformance] = useState(false);
    
    // Add state for force update
    const [forceUpdate, setForceUpdate] = useState(0);
    
    // Add state for chart data (always last 7 days)
    const [chartData, setChartData] = useState([]);
    
    // Add state for trend data
    const [trendData, setTrendData] = useState({});
    
    // Log when showLowPerformance changes
    useEffect(() => {
        console.debug('Main component showLowPerformance changed:', showLowPerformance);
    }, [showLowPerformance]);

    // Create a direct toggle handler for debugging
    const handleToggleLowPerformance = () => {
        console.debug('Direct toggle called, current state:', showLowPerformance);
        setShowLowPerformance(prevState => {
            const newState = !prevState;
            console.debug('Setting new state:', newState);
            return newState;
        });
    };
    
    // Handler for force update with debounce and data check
    const lastUpdateTimeRef = useRef(Date.now());
    const handleForceUpdate = () => {
        // Don't force update if we have no data and it's been less than 5 seconds since last update
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
        
        // Skip rapid updates when we have no data
        if ((reports.length === 0 || last7DaysReports.length === 0) && timeSinceLastUpdate < 5000) {
            console.debug('Skipping force update due to no data and recent update:', {
                reportsLength: reports.length,
                last7DaysReportsLength: last7DaysReports.length,
                timeSinceLastUpdate,
                minDelay: 5000
            });
            return;
        }
        
        lastUpdateTimeRef.current = now;
        setForceUpdate(prev => prev + 1);
        console.debug('Force update triggered:', forceUpdate + 1);
    };

    // Use custom hooks for data fetching and state management
    const { 
        reports, 
        loading, 
        error, 
        totals, 
        fetchReports 
    } = useMediaBuyingReports(dateRange, filters, sortOptions);

    // Create a separate hook instance for chart data (always last 7 days)
    const { 
        reports: last7DaysReports,
        loading: chartLoading,
        fetchReports: fetchLast7DaysReports
    } = useMediaBuyingReports(
        {
            startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
            endDate: new Date(),
            preset: 'last7Days'
        }, 
        filters, 
        sortOptions
    );

    // Calculate previous period date range (same duration as current, but shifted back)
    const previousPeriodDateRange = useMemo(() => {
        const currentStartDate = new Date(dateRange.startDate);
        const currentEndDate = new Date(dateRange.endDate);
        
        // Exclude today from calculations
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // If the end date is today, adjust it to yesterday
        let adjustedEndDate = currentEndDate;
        if (currentEndDate.toDateString() === today.toDateString()) {
            adjustedEndDate = new Date(today);
            adjustedEndDate.setDate(today.getDate() - 1);
        }
        
        // Calculate the duration in days
        const durationMs = adjustedEndDate.getTime() - currentStartDate.getTime();
        const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
        
        // Calculate previous period dates
        const previousEndDate = new Date(currentStartDate);
        previousEndDate.setDate(previousEndDate.getDate() - 1);
        
        const previousStartDate = new Date(previousEndDate);
        previousStartDate.setDate(previousStartDate.getDate() - durationDays);
        
        console.debug('Previous period calculation:', {
            currentPeriod: {
                start: currentStartDate.toISOString(),
                end: adjustedEndDate.toISOString(),
                durationDays
            },
            previousPeriod: {
                start: previousStartDate.toISOString(),
                end: previousEndDate.toISOString()
            }
        });
        
        return {
            startDate: previousStartDate,
            endDate: previousEndDate
        };
    }, [dateRange]);

    // Create a hook instance for previous period data
    const { 
        reports: previousPeriodReports,
        loading: previousPeriodLoading,
        totals: previousPeriodTotals
    } = useMediaBuyingReports(previousPeriodDateRange, filters, sortOptions);

    // Calculate trend data when current or previous period data changes
    useEffect(() => {
        if (!loading && !previousPeriodLoading && totals && previousPeriodTotals) {
            // Calculate trends for metrics
            const metricTrends = calculateMetricTrends(totals, previousPeriodTotals);
            
            // Group current period reports by campaign for health calculation
            const currentGroupedByCampaign = {};
            reports.forEach(report => {
                if (!currentGroupedByCampaign[report.sub_id]) {
                    currentGroupedByCampaign[report.sub_id] = {
                        sub_id: report.sub_id,
                        clicks: 0,
                        conversions: 0,
                        cost: 0,
                        revenue: 0,
                        total_revenue: 0,
                        taboola_clicks: 0
                    };
                }
                
                // Aggregate metrics
                const group = currentGroupedByCampaign[report.sub_id];
                group.clicks += parseFloat(report.clicks) || 0;
                group.conversions += parseFloat(report.conversions) || 0;
                group.cost += parseFloat(report.cost) || 0;
                group.revenue += parseFloat(report.revenue) || 0;
                group.total_revenue += parseFloat(report.total_revenue) || 0;
                group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
            });
            
            // Calculate derived metrics for each current campaign
            Object.values(currentGroupedByCampaign).forEach(group => {
                group.cvr = group.taboola_clicks > 0 ? (group.conversions / group.taboola_clicks) * 100 : 0;
                group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
                group.roas = calculateRoas(group, 'healthTrendCurrent');
            });
            
            // Group previous period reports by campaign
            const previousGroupedByCampaign = {};
            previousPeriodReports.forEach(report => {
                if (!previousGroupedByCampaign[report.sub_id]) {
                    previousGroupedByCampaign[report.sub_id] = {
                        sub_id: report.sub_id,
                        clicks: 0,
                        conversions: 0,
                        cost: 0,
                        revenue: 0,
                        total_revenue: 0,
                        taboola_clicks: 0
                    };
                }
                
                // Aggregate metrics
                const group = previousGroupedByCampaign[report.sub_id];
                group.clicks += parseFloat(report.clicks) || 0;
                group.conversions += parseFloat(report.conversions) || 0;
                group.cost += parseFloat(report.cost) || 0;
                group.revenue += parseFloat(report.revenue) || 0;
                group.total_revenue += parseFloat(report.total_revenue) || 0;
                group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
            });
            
            // Calculate derived metrics for each previous campaign
            Object.values(previousGroupedByCampaign).forEach(group => {
                group.cvr = group.taboola_clicks > 0 ? (group.conversions / group.taboola_clicks) * 100 : 0;
                group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
                group.roas = calculateRoas(group, 'healthTrendPrevious');
            });
            
            // Count low performance campaigns using aggregated metrics
            const currentLowPerformanceCount = Object.values(currentGroupedByCampaign)
                .filter(group => hasLowPerformance(group)).length;
                
            const previousLowPerformanceCount = Object.values(previousGroupedByCampaign)
                .filter(group => hasLowPerformance(group)).length;
            
            // Calculate health percentages
            const currentHealthyPercentage = Object.keys(currentGroupedByCampaign).length > 0 
                ? ((Object.keys(currentGroupedByCampaign).length - currentLowPerformanceCount) / Object.keys(currentGroupedByCampaign).length) * 100 
                : 0;
                
            const previousHealthyPercentage = Object.keys(previousGroupedByCampaign).length > 0 
                ? ((Object.keys(previousGroupedByCampaign).length - previousLowPerformanceCount) / Object.keys(previousGroupedByCampaign).length) * 100 
                : 0;
                
            const healthTrend = previousHealthyPercentage === 0 
                ? 0 
                : ((currentHealthyPercentage - previousHealthyPercentage) / previousHealthyPercentage) * 100;
                
            // Calculate campaigns trend
            const campaignsTrend = Object.keys(previousGroupedByCampaign).length === 0 
                ? 0 
                : ((Object.keys(currentGroupedByCampaign).length - Object.keys(previousGroupedByCampaign).length) / Object.keys(previousGroupedByCampaign).length) * 100;
                
            // Set trend data
            setTrendData({
                ...metricTrends,
                healthTrend,
                campaignsTrend
            });
            
            console.debug('Trend data calculated:', {
                metricTrends,
                healthTrend,
                campaignsTrend,
                currentHealthy: currentHealthyPercentage,
                previousHealthy: previousHealthyPercentage,
                currentCampaigns: Object.keys(currentGroupedByCampaign).length,
                previousCampaigns: Object.keys(previousGroupedByCampaign).length,
                currentLowPerformanceCount,
                previousLowPerformanceCount
            });
        }
    }, [loading, previousPeriodLoading, totals, previousPeriodTotals, reports, previousPeriodReports]);

    const { 
        campaignStatuses, 
        statusLoading, 
        statusError, 
        fetchCampaignStatuses, 
        pauseCampaign, 
        activateCampaign 
    } = useCampaignStatuses();

    // Custom hook for campaign selection - we pass the regular reports for metrics calculation
    // but the actual campaign filtering for the chart will be done in the CampaignTrendsTable component
    // using the last7DaysReports data
    const {
        selectedCampaign,
        lowPerformanceCount,
        healthyPercentage,
        totalCampaigns,
        handleSelectCampaign,
    } = useCampaignSelection(reports, showLowPerformance, setShowLowPerformance);
    
    // Filter reports based on showLowPerformance
    const filteredReports = React.useMemo(() => {
        // First apply the search filter (which is already handled by the useMediaBuyingReports hook)
        // Then apply the low performance filter if needed
        if (!showLowPerformance) {
            console.debug('Low performance filter is OFF - showing all reports (with search filter applied)');
            return reports; // reports are already filtered by search in the hook
        }
        
        console.debug('Filtering reports for low performance...');
        
        // Group reports by sub_id to check aggregated performance
        const groupedByCampaign = {};
        
        // First, group all reports by campaign ID
        reports.forEach(report => {
            if (!groupedByCampaign[report.sub_id]) {
                groupedByCampaign[report.sub_id] = {
                    sub_id: report.sub_id,
                    keyword: report.keyword,
                    clicks: 0,
                    conversions: 0,
                    cost: 0,
                    revenue: 0,
                    total_revenue: 0,
                    taboola_clicks: 0,
                    reports: []
                };
            }
            
            // Aggregate metrics
            const group = groupedByCampaign[report.sub_id];
            group.clicks += parseFloat(report.clicks) || 0;
            group.conversions += parseFloat(report.conversions) || 0;
            group.cost += parseFloat(report.cost) || 0;
            group.revenue += parseFloat(report.revenue) || 0;
            group.total_revenue += parseFloat(report.total_revenue) || 0;
            group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
            
            // Store original report
            group.reports.push(report);
        });
        
        // Calculate derived metrics for each campaign group
        Object.values(groupedByCampaign).forEach(group => {
            // Calculate CVR
            group.cvr = group.taboola_clicks > 0 ? (group.conversions / group.taboola_clicks) * 100 : 0;
            
            // Calculate payout
            group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
            
            // Calculate ROAS using the unified method
            group.roas = calculateRoas(group, 'aggregatedCampaign');
            
            // Debug log for campaign 44123876
            if (group.sub_id === '44123876') {
                console.debug('Aggregated metrics for campaign 44123876:', {
                    cost: group.cost,
                    revenue: group.revenue,
                    roas: group.roas,
                    payout: group.payout,
                    cvr: group.cvr,
                    reportsCount: group.reports.length
                });
            }
        });
        
        // Find campaigns with low aggregated performance
        const lowPerformanceCampaignIds = Object.values(groupedByCampaign)
            .filter(group => hasLowPerformance(group))
            .map(group => group.sub_id);
            
        console.debug(`Found ${lowPerformanceCampaignIds.length} campaigns with low aggregated performance`);
        
        // Only include reports from campaigns with low aggregated performance
        const filtered = reports.filter(report => 
            lowPerformanceCampaignIds.includes(report.sub_id)
        );
        
        console.debug(`Filtered ${reports.length} reports down to ${filtered.length} reports from low performance campaigns`);
        
        // Log some sample reports for debugging
        if (filtered.length > 0) {
            console.debug('First 3 filtered reports:', filtered.slice(0, 3).map(report => ({
                sub_id: report.sub_id,
                date: report.date,
                payout: report.clicks > 0 ? report.total_revenue / report.clicks : 0,
                cvr: parseFloat(report.cvr) || 0,
                roas: calculateRoas(report)
            })));
        }
        
        return filtered;
    }, [reports, showLowPerformance]);
    
    // Filter chart data based on showLowPerformance
    // Note: The campaign filtering will be done in the CampaignTrendsTable component
    // This ensures that regardless of the date range filter, the chart always shows
    // the last 7 days of data, even when filtering by campaign
    const filteredChartData = React.useMemo(() => {
        if (!showLowPerformance) {
            console.debug('Chart data: Low performance filter is OFF - showing all last 7 days reports');
            return last7DaysReports;
        }
        
        console.debug('Filtering chart data for low performance...');
        
        // Group reports by sub_id to check aggregated performance
        const groupedByCampaign = {};
        
        // First, group all reports by campaign ID
        last7DaysReports.forEach(report => {
            if (!groupedByCampaign[report.sub_id]) {
                groupedByCampaign[report.sub_id] = {
                    sub_id: report.sub_id,
                    keyword: report.keyword,
                    clicks: 0,
                    conversions: 0,
                    cost: 0,
                    revenue: 0,
                    total_revenue: 0,
                    taboola_clicks: 0,
                    reports: []
                };
            }
            
            // Aggregate metrics
            const group = groupedByCampaign[report.sub_id];
            group.clicks += parseFloat(report.clicks) || 0;
            group.conversions += parseFloat(report.conversions) || 0;
            group.cost += parseFloat(report.cost) || 0;
            group.revenue += parseFloat(report.revenue) || 0;
            group.total_revenue += parseFloat(report.total_revenue) || 0;
            group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
            
            // Store original report
            group.reports.push(report);
        });
        
        // Calculate derived metrics for each campaign group
        Object.values(groupedByCampaign).forEach(group => {
            // Calculate CVR
            group.cvr = group.taboola_clicks > 0 ? (group.conversions / group.taboola_clicks) * 100 : 0;
            
            // Calculate payout
            group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
            
            // Calculate ROAS using the unified method
            group.roas = calculateRoas(group, 'chartDataAggregated');
            
            // Debug log for campaign 44247723
            if (group.sub_id === '44247723') {
                console.debug('Aggregated metrics for chart data campaign 44247723:', {
                    cost: group.cost,
                    revenue: group.revenue,
                    roas: group.roas,
                    payout: group.payout,
                    cvr: group.cvr,
                    reportsCount: group.reports.length
                });
            }
        });
        
        // Find campaigns with low aggregated performance
        const lowPerformanceCampaignIds = Object.values(groupedByCampaign)
            .filter(group => hasLowPerformance(group))
            .map(group => group.sub_id);
            
        console.debug(`Found ${lowPerformanceCampaignIds.length} campaigns with low aggregated performance in chart data`);
        
        // Only include reports from campaigns with low aggregated performance
        // IMPORTANT: We include ALL days for campaigns with low performance
        const filtered = last7DaysReports.filter(report => 
            lowPerformanceCampaignIds.includes(report.sub_id)
        );
        
        console.debug(`Filtered ${last7DaysReports.length} chart reports down to ${filtered.length} reports from low performance campaigns`);
        
        // Log the unique campaigns and dates in the filtered data
        const uniqueCampaigns = [...new Set(filtered.map(report => report.sub_id))];
        const uniqueDates = [...new Set(filtered.map(report => report.date))];
        console.debug('Filtered chart data contains:', {
            uniqueCampaigns,
            uniqueDatesCount: uniqueDates.length,
            firstDate: uniqueDates[0],
            lastDate: uniqueDates[uniqueDates.length - 1]
        });
        
        return filtered;
    }, [last7DaysReports, showLowPerformance]);
    
    // Add an effect to verify filtered reports
    useEffect(() => {
        if (showLowPerformance) {
            console.debug('VERIFICATION: Filtered reports check');
            
            // Group reports by campaign ID for verification
            const groupedByCampaign = {};
            
            // First, group all filtered reports by campaign ID
            filteredReports.forEach(report => {
                if (!groupedByCampaign[report.sub_id]) {
                    groupedByCampaign[report.sub_id] = {
                        sub_id: report.sub_id,
                        keyword: report.keyword,
                        clicks: 0,
                        conversions: 0,
                        cost: 0,
                        revenue: 0,
                        total_revenue: 0,
                        taboola_clicks: 0,
                        reports: []
                    };
                }
                
                // Aggregate metrics
                const group = groupedByCampaign[report.sub_id];
                group.clicks += parseFloat(report.clicks) || 0;
                group.conversions += parseFloat(report.conversions) || 0;
                group.cost += parseFloat(report.cost) || 0;
                group.revenue += parseFloat(report.revenue) || 0;
                group.total_revenue += parseFloat(report.total_revenue) || 0;
                group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
                
                // Store original report
                group.reports.push(report);
            });
            
            // Calculate derived metrics for each campaign group
            Object.values(groupedByCampaign).forEach(group => {
                // Calculate CVR
                group.cvr = group.taboola_clicks > 0 ? (group.conversions / group.taboola_clicks) * 100 : 0;
                
                // Calculate payout
                group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
                
                // Calculate ROAS using the unified method
                group.roas = calculateRoas(group, 'verificationAggregated');
            });
            
            // Verify that all aggregated campaigns have low performance
            const allLowPerformance = Object.values(groupedByCampaign).every(group => hasLowPerformance(group));
            console.debug(`VERIFICATION: All aggregated campaigns have low performance: ${allLowPerformance}`);
            
            if (!allLowPerformance) {
                // Find campaigns that don't have low performance
                const nonLowPerformanceCampaigns = Object.values(groupedByCampaign).filter(group => !hasLowPerformance(group));
                console.debug(`VERIFICATION: Found ${nonLowPerformanceCampaigns.length} campaigns that don't have low performance`);
                
                if (nonLowPerformanceCampaigns.length > 0) {
                    console.debug('VERIFICATION: First non-low-performance campaign:', {
                        sub_id: nonLowPerformanceCampaigns[0].sub_id,
                        payout: nonLowPerformanceCampaigns[0].payout,
                        cvr: nonLowPerformanceCampaigns[0].cvr,
                        roas: nonLowPerformanceCampaigns[0].roas,
                        reportCount: nonLowPerformanceCampaigns[0].reports.length
                    });
                }
            }
        }
    }, [filteredReports, showLowPerformance]);

    // Event handlers
    const handleDateFilterChange = (newDateRange) => {
        console.debug('Date range changed:', newDateRange);
        
        const today = new Date();
        let startDate, endDate;

        switch (newDateRange.preset) {
            case 'yesterday':
                // For yesterday, both start and end dates should be yesterday
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);
                
                startDate = new Date(yesterday);
                endDate = new Date(yesterday);
                endDate.setHours(23, 59, 59, 999);
                
                console.debug('Setting yesterday date range:', {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    startDateFormatted: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
                    endDateFormatted: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
                });
                break;
                
            case 'today':
                // For today, both start and end dates should be today
                const todayDate = new Date(today);
                todayDate.setHours(0, 0, 0, 0);
                
                startDate = new Date(todayDate);
                endDate = new Date(todayDate);
                endDate.setHours(23, 59, 59, 999);
                
                console.debug('Setting today date range:', {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    startDateFormatted: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
                    endDateFormatted: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
                });
                break;
                
            case 'last7Days':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(today);
                endDate.setHours(23, 59, 59, 999);
                break;
                
            case 'last30Days':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(today);
                endDate.setHours(23, 59, 59, 999);
                break;
                
            default:
                startDate = newDateRange.startDate;
                endDate = newDateRange.endDate;
                break;
        }

        const updatedDateRange = { 
            startDate, 
            endDate, 
            preset: newDateRange.preset 
        };
        
        console.debug('Setting new date range:', updatedDateRange);
        console.debug('Date objects:', {
            startDateObj: startDate,
            endDateObj: endDate,
            startISOString: startDate?.toISOString(),
            endISOString: endDate?.toISOString(),
            startDate: startDate ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}` : null,
            endDate: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : null
        });
        
        // Update the state
        setDateRange(updatedDateRange);
        
        // Directly call fetchReports with the updated date range to ensure the API call is made with the correct dates
        console.debug('Directly calling fetchReports after date range change');
        setTimeout(() => {
            console.debug('Executing fetchReports with updated dateRange:', {
                startDate: updatedDateRange.startDate?.toISOString(),
                endDate: updatedDateRange.endDate?.toISOString(),
                preset: updatedDateRange.preset,
                startDateFormatted: updatedDateRange.startDate ? `${updatedDateRange.startDate.getFullYear()}-${String(updatedDateRange.startDate.getMonth() + 1).padStart(2, '0')}-${String(updatedDateRange.startDate.getDate()).padStart(2, '0')}` : null,
                endDateFormatted: updatedDateRange.endDate ? `${updatedDateRange.endDate.getFullYear()}-${String(updatedDateRange.endDate.getMonth() + 1).padStart(2, '0')}-${String(updatedDateRange.endDate.getDate()).padStart(2, '0')}` : null
            });
            // Pass the updated date range directly to fetchReports
            fetchReports(updatedDateRange);
        }, 0);
    };
    
    const handleFiltersChange = (newFilters) => {
        console.debug('Filters changed:', {
            oldFilters: filters,
            newFilters: newFilters
        });
        setFilters(newFilters);
    };
    
    const handleSortOptionsChange = (newSortOptions) => {
        setSortOptions(newSortOptions);
    };

    // Handle sort change
    const handleSortChange = (field, direction) => {
        console.debug('Sort changed:', { field, direction });
        setSortOptions({
            field,
            direction
        });
    };

    // Handle search input change
    const handleSearchChange = (searchTerm) => {
        console.debug('Search term changed:', searchTerm);
        setFilters(prevFilters => ({
            ...prevFilters,
            search: searchTerm
        }));
    };

    // Refresh both regular reports and chart data (last 7 days)
    const handleRefresh = () => {
        fetchReports();
        fetchLast7DaysReports();
    };

    // Log when component renders
    console.debug('MediaBuyingReportPanel rendering:', {
        showLowPerformance,
        forceUpdate,
        reportsLength: reports?.length || 0,
        filteredReportsLength: filteredReports?.length || 0,
        chartDataLength: last7DaysReports?.length || 0,
        selectedCampaign,
        searchFilter: filters.search || 'none'
    });

    // Handle errors
    if (error) {
        return (
            <MainLayoutDemand>
                <Typography color="error">{error}</Typography>
            </MainLayoutDemand>
        );
    }

    return (
        <Box sx={{ 
            background: 'linear-gradient(to bottom, #121212, #1a1a2e)',
            minHeight: '100vh',
            pt: 4,
            pb: 4
        }}>
            <Container maxWidth="xl">
                <Paper elevation={3} sx={{ 
                    p: 4, 
                    borderRadius: '12px', 
                    bgcolor: '#1a1a2e',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}>
                    {/* Dashboard Header */}
                    <DashboardHeader 
                        dateRange={dateRange}
                        onDateRangeChange={handleDateFilterChange}
                        onRefresh={handleRefresh}
                        showLowPerformance={showLowPerformance}
                        onToggleLowPerformance={handleToggleLowPerformance}
                        lowPerformanceCount={lowPerformanceCount}
                    />
                    
                    <Grid container spacing={0}>
                        {/* Metrics Cards */}
                        
                        
                        {/* Campaign Trends Table - Replace Chart with Table */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <CampaignTrendsTable 
                                key={last7DaysReports.length > 0 ? `campaign-trends-${selectedCampaign}-${forceUpdate}` : 'campaign-trends-no-data'}
                                data={filteredChartData} 
                                selectedCampaign={selectedCampaign} 
                                loading={chartLoading}
                                isAdmin={isAdmin}
                            />
                        </Grid>
                        
                        {/* Campaign Table */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            {/* Table Controls */}
                            <TableControls 
                                showLowPerformance={showLowPerformance}
                                onToggleLowPerformance={handleToggleLowPerformance}
                                filters={filters}
                                onFiltersChange={handleFiltersChange}
                                statusLoading={statusLoading}
                                onRefreshStatuses={fetchCampaignStatuses}
                                onForceUpdate={handleForceUpdate}
                                totalReports={reports.length}
                                filteredReports={filteredReports.length}
                                sx={{ mb: 2 }}
                            />
                            
                            {/* Campaign Table */}
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#1e2130', borderRadius: '8px', mt: 0 }}>
                                    <CircularProgress sx={{ color: 'white' }} />
                                </Box>
                            ) : (
                                <>
                                    <CampaignTable 
                                        key={reports.length > 0 ? 
                                            `campaign-table-${showLowPerformance}-${forceUpdate}-${dateRange.startDate?.toISOString()}-${dateRange.endDate?.toISOString()}` : 
                                            'campaign-table-no-data'}
                                        reports={filteredReports} 
                                        onSelectCampaign={handleSelectCampaign} 
                                        selectedCampaign={selectedCampaign}
                                        campaignStatuses={campaignStatuses}
                                        onPauseCampaign={pauseCampaign}
                                        onActivateCampaign={activateCampaign}
                                        isFiltered={showLowPerformance}
                                        totalReportsCount={reports.length}
                                        dateRange={dateRange}
                                    />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </Box>
    );
};

export default MediaBuyingReportPanel;